import React, { useEffect, useState } from "react";
import PaceReportIcon from "assets/svgIcons/AdminDashboard/PaceReportIcon";
import { useTranslation } from "react-i18next";
import PaceIcon from "assets/svgIcons/MapLegendPopup/PaceAlert/PaceIcon";
import { useSelector, useDispatch } from "react-redux";
import { useHookstate } from "@hookstate/core";
import { DASHBOARD_GLOBAL_STATE, SITEID_GLOBAL_STATE, UPDATED_CAR_DATA_STATE, GlobalCourseIdState } from "globalStore/globalStore";
import { isCarInUse } from "_helperFunctions/isCarInUse";
import { PaceReportModal } from "./PaceReportModal";
import { COMMON_SERVICES } from "redux/reduxToolkitServices/_commonServices/commonServices";

// Constants for pace status colors and icons
const PACE_STATUS = {
  CRITICAL: {
    color: "#FA3C30",
    translationKey: "Critically_behind_pace",
    className: "see-red"
  },
  ON_PACE: {
    color: "#00A692",
    translationKey: "Car_on_pace",
    className: "see-green"
  },
  WARNING: {
    color: "#FFE500",
    translationKey: "Falling_behind_pace",
    className: "see-yellow"
  }
};

// Reusable pace status component
const PaceStatusCard = ({ status, count }) => {
  const { t } = useTranslation();

  return (
    <div>
      <p>
        <PaceIcon width="46px" height="45px" color={PACE_STATUS[status].color} />
        {t(PACE_STATUS[status].translationKey)}
      </p>
      <h2 className={PACE_STATUS[status].className}>{count}</h2>
    </div>
  );
};

export const PaceReports = () => {
  const { t } = useTranslation();
  const { siteSettingsData } = useSelector((state) => state?.commonReducers) || [];
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [paceStats, setPaceStats] = useState(false);
  const { paceReportData } = useHookstate(DASHBOARD_GLOBAL_STATE);
  const {updatedCarData} = useHookstate(UPDATED_CAR_DATA_STATE);
  const {globalCourseId} = useHookstate(GlobalCourseIdState);
  const { siteId } = useHookstate(SITEID_GLOBAL_STATE);
  const dispatch = useDispatch();
  const [getCurrentCarsData, setGetCurrentCarsData] = useState([]);

  useEffect(() => {
      const rawData = updatedCarData.get();
      // Check if rawData is a valid non-empty string before parsing
      if (rawData && typeof rawData === 'string' && rawData.trim() !== '') {
        setGetCurrentCarsData(JSON.parse(rawData));
      }
  }, [updatedCarData.get()]);

  useEffect(() => {
    dispatch(COMMON_SERVICES?.getCarServices(false, siteId.get()));
  }, [siteId.get()]);
  
  // Memoized pace calculations for better performance
  useEffect(() => {
    if (!getCurrentCarsData || !siteSettingsData) {
      return { critical: 0, warning: 0, onPace: 0 };
    }
    var counts = getCurrentCarsData.reduce((counts, car) => {
      if (Number(car?.Car?.Course) === Number(globalCourseId.get()) && isCarInUse(car?.Car, false, false, false)) {
        const pace = Math.abs(car?.Car?.Pace || 0);

        if (pace >= siteSettingsData.CriticalThreshold) {
          counts.critical++;
        } else if (pace >= siteSettingsData.WarningThreshold) {
          counts.warning++;
        } else {
          counts.onPace++;
        }
      }
      return counts;
    }, { critical: 0, warning: 0, onPace: 0 });
    setPaceStats(counts);
  }, [getCurrentCarsData, siteSettingsData,globalCourseId.get()]);

  // Effect to handle modal opening based on global state
  useEffect(() => {
    if (paceReportData.get() === "pace-report") {
      setOpenAlertModal(true);
    }
  }, [paceReportData]);

  return (
    <>
      <PaceReportModal
        openAlertModal={openAlertModal}
        setOpenAlertModal={setOpenAlertModal}
        paceStats={paceStats}
      />
      <div className="header">
        <PaceReportIcon />
        <p>{t("Current_Pace")}</p>
      </div>
      <div data-section="pace-report" className="box-body action-zone-infraction">
        <PaceStatusCard status="CRITICAL" count={paceStats.critical} />
        <PaceStatusCard status="WARNING" count={paceStats.warning} />
        <PaceStatusCard status="ON_PACE" count={paceStats.onPace} />
      </div>
    </>
  );
};
